import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Database, Download, Upload, ShieldCheck, Terminal } from 'lucide-react';
import ExportTab from './DbTools/ExportTab';
import ImportTab from './DbTools/ImportTab';
import QueryTab  from './DbTools/QueryTab';
import AuditTable from './DbTools/AuditTable';
import { adminGet, errMsg } from './DbTools/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Page shell: tab switcher + audit log. Each tab is a focused, ~200-line
// component. The setup payload (`/admin/db/exportable`) drives both tabs'
// dropdowns so the allowlist is never duplicated in JS.
export default function DbTools() {
  const [tab, setTab] = useState('export');
  const [setup, setSetup] = useState({ collections: [], formats: [] });
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [audit, setAudit] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(true);

  const loadSetup = async () => {
    try {
      const res = await adminGet(`${API}/api/admin/db/exportable`);
      setSetup(res.data || { collections: [], formats: [] });
    } catch (e) {
      toast.error(await errMsg(e, 'Failed to load DB tools'));
    } finally {
      setLoadingSetup(false);
    }
  };

  const loadAudit = async () => {
    setLoadingAudit(true);
    try {
      const res = await adminGet(`${API}/api/admin/db/audit?limit=20`);
      setAudit(res.data || []);
    } catch (e) {
      console.debug('audit load failed (non-fatal):', e?.message || e);
    } finally { setLoadingAudit(false); }
  };

  useEffect(() => { loadSetup(); loadAudit(); }, []);

  return (
    <div data-testid="db-tools-page" className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-black font-heading uppercase flex items-center gap-3">
          <Database className="w-9 h-9 text-emerald-600" />
          DB Tools
        </h1>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold uppercase tracking-wide">
          <ShieldCheck className="w-3.5 h-3.5" /> Administrator only
        </span>
      </div>

      <div className="flex gap-2 mb-6" data-testid="db-tab-switcher">
        <TabButton active={tab === 'export'} onClick={() => setTab('export')}
                   icon={Download} label="Export"
                   activeClass="bg-emerald-600 text-white border-emerald-600"
                   testId="tab-export" />
        <TabButton active={tab === 'import'} onClick={() => setTab('import')}
                   icon={Upload} label="Import"
                   activeClass="bg-indigo-600 text-white border-indigo-600"
                   testId="tab-import" />
        <TabButton active={tab === 'query'} onClick={() => setTab('query')}
                   icon={Terminal} label="Query"
                   activeClass="bg-amber-600 text-white border-amber-600"
                   testId="tab-query" />
      </div>

      <div className="mb-6">
        {tab === 'export' ? <ExportTab setup={setup} loadingSetup={loadingSetup} onAfter={loadAudit} />
         : tab === 'import' ? <ImportTab setup={setup} onAfter={loadAudit} />
         : <QueryTab setup={setup} onAfter={loadAudit} />}
      </div>

      <AuditTable rows={audit} loading={loadingAudit} />
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, activeClass, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
        active ? activeClass : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
      }`}
    >
      <Icon className="w-4 h-4 inline mr-1" /> {label}
    </button>
  );
}
